import { all, select, takeEvery, fork, call, put } from 'redux-saga/effects'

// action
import { boxActions } from '@redux/store/index'

// api
import { boxApi, ResType } from '@api'

// lib
import { Logger } from '@utils'

// type
import { Box, ProductPackaging } from '@schemas'
import { BoxStateType } from '@redux/types'

const apiGetBox = async (): Promise<ResType | undefined> => await boxApi.getBox()
const apiGetPackaging = async (): Promise<ResType | undefined> =>
	await boxApi.getProductPackaging()

function* fetchBoxList() {
	try {
		yield put(boxActions.BOX_LIST_STATUS('loading'))
		const apiRes: ResType = yield call(apiGetBox)
		if (apiRes.status === 200) {
			const list = (apiRes.data as any[]).map(a => new Box(a))
			yield put(boxActions.BOX_LIST(list))
			yield put(boxActions.BOX_LIST_STATUS(list.length > 0 ? 'ok' : 'empty'))
		} else {
			yield put(boxActions.BOX_LIST_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* fetchPackagingList() {
	try {
		yield put(boxActions.PACKAGING_LIST_STATUS('loading'))
		const apiRes: ResType = yield call(apiGetPackaging)
		if (apiRes.status === 200) {
			const list = (apiRes.data as any[]).map(a => new ProductPackaging(a))
			yield put(boxActions.PACKAGING_LIST(list))
			yield put(boxActions.PACKAGING_LIST_STATUS(list.length > 0 ? 'ok' : 'empty'))
		} else {
			yield put(boxActions.PACKAGING_LIST_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerInitBox() {
	yield all([call(fetchBoxList), call(fetchPackagingList)])
}

function* updateBox() {
	try {
		const body: BoxStateType['updateBox'] = yield select(state => state.box.updateBox)
		const fields = {
			code: body.code,
			name: body.name,
			widthCm: Number(body.widthCm),
			heightCm: Number(body.heightCm),
			depthCm: Number(body.depthCm),
			tareKg: Number(body.tareKg),
			useYn: Number(body.useYn) === 0 ? 0 : 1,
			memo: body.memo ?? '',
		}

		let res: ResType | undefined
		switch (body.iudFlag) {
			case 'I':
				res = yield call(boxApi.addBox, { body: fields })
				break
			case 'U':
				res = yield call(boxApi.updateBox, {
					params: { boxId: body.boxId },
					body: fields,
				})
				break
			case 'D':
				res = yield call(boxApi.deleteBox, { params: { boxId: body.boxId } })
				break
		}
		if (res !== undefined && res.status >= 400) {
			const data: any = res.data
			const msg = data?.message ?? data
			const title = msg?.meta?.title
			yield put(
				boxActions.ERROR(
					Array.isArray(title)
						? String(title[0])
						: typeof title === 'string'
						? title
						: msg?.errorMessage ?? '저장 실패',
				),
			)
		}

		yield all([call(fetchBoxList), call(fetchPackagingList)])
	} catch (error) {
		Logger.warn(error)
	}
}

function* updatePackaging() {
	try {
		const item: BoxStateType['updatePackaging'] = yield select(
			state => state.box.updatePackaging,
		)
		if (item.productId === '') return
		yield call(boxApi.updateProductPackaging, {
			body: {
				items: [
					{
						productId: item.productId,
						boxId: item.boxId ?? '',
						netKg:
							item.netKg === null || item.netKg === undefined || String(item.netKg) === ''
								? null
								: Number(item.netKg),
					},
				],
			},
		})
		yield all([call(fetchBoxList), call(fetchPackagingList)])
	} catch (error) {
		Logger.warn(error)
	}
}

function* mainSaga() {
	yield takeEvery(boxActions.TRIGGER_INIT_BOX_PAGE, triggerInitBox)
	yield takeEvery(boxActions.UPDATE_BOX, updateBox)
	yield takeEvery(boxActions.UPDATE_PACKAGING, updatePackaging)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
